import React, { useCallback, useMemo, useState } from 'react';

import { useHistory } from 'react-router-dom';

import {
  AutoAwesome as AiIcon,
  ExpandMore as ExpandMoreIcon,
  TableRows as FlatViewIcon,
  AccountTree as GroupedViewIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import {
  useGetAttractionCities,
  useGetAttractionStats,
  useListAttractions,
  useUpdateAttractionActive,
} from '~/hooks/apis/attractions';

import {
  ATTRACTION_TYPES,
  type Attraction,
  type AttractionType,
  type GetAttractionStatsParams,
  type ListAttractionsParams,
  formatAttractionType,
} from '~/types/attractionTypes';

const DEFAULT_PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: 'popularity', label: 'Popularity' },
  { value: 'most_offers', label: 'Most Offers' },
  { value: 'name', label: 'Name' },
] as const;

/** Returns MUI color token based on coverage rate thresholds */
function coverageColor(rate: number): 'success' | 'warning' | 'error' {
  if (rate > 70) return 'success';
  if (rate > 40) return 'warning';
  return 'error';
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
}

export default function AttractionsPage() {
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;
  const history = useHistory();

  const activeMutation = useUpdateAttractionActive();

  // Pagination state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Filter inputs (not yet applied)
  const [countryInput, setCountryInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [typeInput, setTypeInput] = useState<AttractionType | ''>('');
  const [sortInput, setSortInput] = useState<string>('popularity');

  // Applied filters (triggers query)
  const [filters, setFilters] = useState<ListAttractionsParams>({
    sort: 'popularity',
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
  });

  // Fetch cities for the filter dropdowns
  const { data: citiesData } = useGetAttractionCities();
  const cities = citiesData?.result ?? [];

  // Derive unique countries from cities data
  const countries = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cities) {
      if (c.country && c.countryCode) map.set(c.country, c.countryCode);
    }
    return Array.from(map.entries())
      .map(([country, countryCode]) => ({ country, countryCode }))
      .sort((a, b) => a.country.localeCompare(b.country));
  }, [cities]);

  // Filter cities by selected country
  const filteredCities = useMemo(
    () => (countryInput ? cities.filter((c) => c.country === countryInput) : cities),
    [cities, countryInput],
  );

  // Stats params derived from applied filters
  const statsParams: GetAttractionStatsParams = useMemo(() => {
    const params: GetAttractionStatsParams = {};
    if (filters.country) {
      const match = countries.find((countryOption) => countryOption.country === filters.country);
      if (match) params.countryCode = match.countryCode;
    }
    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.city) params.city = filters.city;
    if (filters.type) params.type = filters.type;
    return params;
  }, [filters, countries]);

  const hasActiveFilters = Object.values(statsParams).some(Boolean);
  const { data: statsData } = useGetAttractionStats(statsParams);
  const stats = statsData?.result ?? null;

  // View mode: flat grid vs grouped accordion
  const [viewMode, setViewMode] = useState<'flat' | 'grouped'>('flat');
  const groupedStatsParams: GetAttractionStatsParams = useMemo(
    () => ({ ...statsParams, groupBy: 'city' as const }),
    [statsParams],
  );
  const { data: groupedData, isLoading: isGroupedLoading } = useGetAttractionStats(groupedStatsParams, {
    enabled: viewMode === 'grouped',
  });
  const groupedStats = viewMode === 'grouped' ? groupedData?.result ?? null : null;

  // Active filter labels for breadcrumbs
  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onDelete: () => void }> = [];
    if (filters.country) {
      chips.push({
        key: 'country',
        label: `Country: ${filters.country}`,
        onDelete: () => {
          setCountryInput('');
          setCityInput('');
          setFilters((prev) => ({ ...prev, country: undefined, countryCode: undefined, city: undefined, offset: 0 }));
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        },
      });
    }
    if (filters.city) {
      chips.push({
        key: 'city',
        label: `City: ${filters.city}`,
        onDelete: () => {
          setCityInput('');
          setFilters((prev) => ({ ...prev, city: undefined, offset: 0 }));
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        },
      });
    }
    if (filters.type) {
      chips.push({
        key: 'type',
        label: `Type: ${formatAttractionType(filters.type)}`,
        onDelete: () => {
          setTypeInput('');
          setFilters((prev) => ({ ...prev, type: undefined, offset: 0 }));
          setPaginationModel((prev) => ({ ...prev, page: 0 }));
        },
      });
    }
    return chips;
  }, [filters]);

  // Build query params from pagination + filters
  const queryParams: ListAttractionsParams = useMemo(
    () => ({
      ...filters,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    }),
    [paginationModel, filters],
  );

  const { data, isLoading, isFetching, error } = useListAttractions(queryParams);

  const attractions: Attraction[] = data?.result ?? [];
  const totalCount = data?.total ?? 0;

  /** Apply search filters and reset to first page */
  const handleSearch = useCallback(() => {
    setFilters({
      country: countryInput || undefined,
      city: cityInput || undefined,
      type: typeInput || undefined,
      sort: sortInput || undefined,
      limit: paginationModel.pageSize,
      offset: 0,
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [countryInput, cityInput, typeInput, sortInput, paginationModel.pageSize]);

  /** Clear all filters */
  const handleClearFilters = useCallback(() => {
    setCountryInput('');
    setCityInput('');
    setTypeInput('');
    setSortInput('popularity');
    setFilters({ sort: 'popularity', limit: DEFAULT_PAGE_SIZE, offset: 0 });
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  }, []);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  }, []);

  const handleToggleActive = useCallback(
    (attractionId: string, currentActive: boolean) => {
      activeMutation.mutate({ attractionId, active: !currentActive });
    },
    [activeMutation],
  );

  const columns: Array<GridColDef<Attraction>> = useMemo(
    () => [
      {
        field: 'actions',
        headerName: 'View',
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: GridRenderCellParams<Attraction>) => (
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                history.push(`/attractions/${row.attractionId}`);
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: 'imageUrl',
        headerName: 'Image',
        width: 70,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ value }: GridRenderCellParams<Attraction>) =>
          value ? (
            <Box
              component="img"
              src={
                (value as string).startsWith('http')
                  ? (value as string)
                  : `${window.configs.IMAGE_HOST}/fl_progressive,q_auto:eco,c_fill,g_auto,w_100,h_100/${
                      value as string
                    }`
              }
              alt=""
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement?.setAttribute('data-fallback', 'true');
              }}
              sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1 }}
            />
          ) : (
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 1,
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                N/A
              </Typography>
            </Box>
          ),
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }: GridRenderCellParams<Attraction>) => (
          <Box sx={{ overflow: 'hidden', py: 0.5 }}>
            <Typography variant="body2" fontWeight="bold" noWrap>
              {row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {row.attractionId}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'city',
        headerName: 'City / Country',
        flex: 0.8,
        minWidth: 160,
        renderCell: ({ row }: GridRenderCellParams<Attraction>) => (
          <Typography variant="body2">
            {row.city}, {row.country}
          </Typography>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 140,
        renderCell: ({ value }: GridRenderCellParams<Attraction>) => (
          <Chip label={formatAttractionType(value as string)} size="small" />
        ),
      },
      {
        field: 'offerCount',
        headerName: 'Offers',
        width: 80,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'popularityRank',
        headerName: 'Popularity',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }: GridRenderCellParams<Attraction>) => (
          <Typography variant="body2">#{value as number}</Typography>
        ),
      },
      {
        field: 'active',
        headerName: 'Active',
        width: 80,
        renderCell: ({ row }: GridRenderCellParams<Attraction>) => (
          <Switch
            checked={row.active}
            size="small"
            onChange={(event) => {
              event.stopPropagation();
              handleToggleActive(row.attractionId, row.active);
            }}
            onClick={(event) => event.stopPropagation()}
          />
        ),
      },
    ],
    [handleToggleActive, history],
  );

  // Build KPI stat items for the overview grid
  const statItems = useMemo(() => {
    if (!stats) return [];
    return [
      {
        key: 'attractions',
        value: stats.totalAttractions.toLocaleString(),
        label: 'Total Attractions',
        tooltip: 'Total points of interest matching current filters',
      },
      {
        key: 'coverage',
        value: `${stats.coverageRate}%`,
        label: 'Offer Coverage',
        tooltip: 'Percentage of attractions with at least one offer linked',
        highlight: true,
        color: `${coverageColor(stats.coverageRate)}.main`,
      },
      {
        key: 'covered',
        value: stats.totalCovered.toLocaleString(),
        label: 'With Offers',
        tooltip: 'Attractions with at least one experience offer linked',
      },
      {
        key: 'offers',
        value: stats.totalOffers.toLocaleString(),
        label: 'Offer Links',
        tooltip: 'Total offer-attraction links across all matches',
      },
      {
        key: 'cities',
        value: String(stats.totalCities),
        label: 'Cities',
        tooltip: 'Number of cities with attractions matching current filters',
      },
      {
        key: 'countries',
        value: String(stats.totalCountries),
        label: 'Countries',
        tooltip: 'Number of countries with attractions matching current filters',
      },
    ];
  }, [stats]);

  return (
    <Box>
      {/* Analytics Panel */}
      {stats && (
        <Paper elevation={1} sx={{ p: 2.5, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: hasActiveFilters ? 1.5 : 2.5 }}>
            <AiIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
              {hasActiveFilters ? 'Filtered Analytics' : 'Global Overview'}
            </Typography>
          </Stack>
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
              {activeFilterChips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  size="small"
                  onDelete={chip.onDelete}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          )}

          {/* KPI stat cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: 2,
            }}
          >
            {statItems.map((item) => {
              const isHighlight = item.highlight;
              const bgColor = isHighlight
                ? alpha(theme.palette[coverageColor(stats.coverageRate)].main, 0.06)
                : alpha(themeColor, 0.03);
              const borderColor = isHighlight
                ? alpha(theme.palette[coverageColor(stats.coverageRate)].main, 0.25)
                : alpha(themeColor, 0.1);

              return (
                <Tooltip key={item.key} title={item.tooltip} arrow>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: bgColor,
                      border: '1px solid',
                      borderColor,
                      textAlign: 'center',
                      cursor: 'help',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: isHighlight
                          ? theme.palette[coverageColor(stats.coverageRate)].main
                          : alpha(themeColor, 0.3),
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      color={item.color ?? 'text.primary'}
                      sx={{ lineHeight: 1.2, mb: 0.5 }}
                    >
                      {item.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                      {item.label}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {/* Coverage by Country tiles */}
          {stats.countries.length > 1 && (
            <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="medium"
                sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                Coverage by Country
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 1.5,
                }}
              >
                {[...stats.countries]
                  .sort((a, b) => b.coverageRate - a.coverageRate)
                  .map((countryRow) => {
                    const color = coverageColor(countryRow.coverageRate);
                    const paletteColor = theme.palette[color].main;

                    return (
                      <Box
                        key={countryRow.countryCode}
                        sx={{
                          position: 'relative',
                          overflow: 'hidden',
                          p: 1.5,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: alpha(paletteColor, 0.2),
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            borderColor: paletteColor,
                            bgcolor: alpha(paletteColor, 0.04),
                            transform: 'translateY(-1px)',
                            boxShadow: `0 2px 8px ${alpha(paletteColor, 0.15)}`,
                          },
                        }}
                        onClick={() => {
                          const match = countries.find((c) => c.countryCode === countryRow.countryCode);
                          if (match) {
                            setCountryInput(match.country);
                            setCityInput('');
                            setFilters((prev) => ({
                              ...prev,
                              country: match.country,
                              city: undefined,
                              offset: 0,
                            }));
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                          }
                        }}
                      >
                        {/* Background fill bar */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            bottom: 0,
                            width: `${countryRow.coverageRate}%`,
                            bgcolor: alpha(paletteColor, 0.06),
                            transition: 'width 0.4s ease',
                          }}
                        />
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ position: 'relative' }}
                        >
                          <Typography variant="body2" fontWeight="medium" noWrap sx={{ flex: 1, mr: 1 }}>
                            {countryRow.country}
                          </Typography>
                          <Chip
                            label={`${countryRow.coverageRate}%`}
                            size="small"
                            color={color}
                            sx={{ fontWeight: 'bold', minWidth: 52 }}
                          />
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ position: 'relative', display: 'block', mt: 0.5 }}
                        >
                          {countryRow.coveredCount}/{countryRow.attractionCount} attractions covered
                        </Typography>
                      </Box>
                    );
                  })}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <Autocomplete
            options={countries}
            getOptionLabel={(option) => option.country}
            value={countries.find((c) => c.country === countryInput) ?? null}
            onChange={(_, value) => {
              setCountryInput(value?.country ?? '');
              setCityInput('');
            }}
            renderInput={(params) => <TextField {...params} label="Country" />}
            sx={{ minWidth: 200 }}
            size="small"
          />
          <Autocomplete
            options={filteredCities}
            getOptionLabel={(option) => `${option.city} (${option.attractionCount})`}
            value={filteredCities.find((c) => c.city === cityInput) ?? null}
            onChange={(_, value) => setCityInput(value?.city ?? '')}
            renderInput={(params) => <TextField {...params} label="City" />}
            sx={{ minWidth: 220 }}
            size="small"
          />
          <Autocomplete
            options={[...ATTRACTION_TYPES]}
            getOptionLabel={(option) => formatAttractionType(option)}
            value={(typeInput as AttractionType) || null}
            onChange={(_, value) => setTypeInput((value as AttractionType) ?? '')}
            renderInput={(params) => <TextField {...params} label="Type" />}
            sx={{ minWidth: 180 }}
            size="small"
          />
          <TextField
            select
            label="Sort"
            value={sortInput}
            onChange={(event) => {
              const newSort = event.target.value;
              setSortInput(newSort);
              setFilters((prev) => ({ ...prev, sort: newSort || undefined, offset: 0 }));
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            sx={{ minWidth: 140 }}
            size="small"
          >
            {SORT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" size="small" onClick={handleSearch}>
              Search
            </Button>
            <Button variant="text" size="small" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Error alert */}
      {error && (
        <Box mb={3}>
          <Alert severity="error">
            Error loading attractions: {error instanceof Error ? error.message : String(error)}
          </Alert>
        </Box>
      )}

      {/* View mode toggle with labels */}
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          View:
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, value) => {
            if (value) setViewMode(value);
          }}
          size="small"
        >
          <ToggleButton value="flat" sx={{ px: 1.5, gap: 0.5 }}>
            <FlatViewIcon fontSize="small" />
            <Typography variant="caption" sx={{ textTransform: 'none' }}>
              List
            </Typography>
          </ToggleButton>
          <ToggleButton value="grouped" sx={{ px: 1.5, gap: 0.5 }}>
            <GroupedViewIcon fontSize="small" />
            <Typography variant="caption" sx={{ textTransform: 'none' }}>
              Grouped
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Flat: Attractions data grid */}
      {viewMode === 'flat' && (
        <Paper elevation={0} sx={{ height: 650 }}>
          <DataGridPro
            rows={attractions}
            columns={columns}
            loading={isLoading || isFetching}
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10, 25, 50]}
            rowCount={totalCount}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            rowHeight={60}
            sx={{
              height: '100%',
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: alpha(themeColor, 0.08),
              },
            }}
          />
        </Paper>
      )}

      {/* Grouped: Country > City accordion */}
      {viewMode === 'grouped' && (
        <Box>
          {isGroupedLoading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
          {[...(groupedStats?.countries ?? [])]
            .sort((a, b) => b.attractionCount - a.attractionCount)
            .map((country) => {
              const color = coverageColor(country.coverageRate);
              const paletteColor = theme.palette[color].main;

              return (
                <Accordion
                  key={country.countryCode}
                  disableGutters
                  elevation={0}
                  sx={{
                    mb: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      bgcolor: alpha(paletteColor, 0.04),
                      borderBottom: '1px solid',
                      borderColor: alpha(paletteColor, 0.1),
                      minHeight: 56,
                      '&.Mui-expanded': { minHeight: 56 },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ minWidth: 180 }}>
                        {country.country}
                      </Typography>
                      <Chip
                        label={`${country.coverageRate}%`}
                        size="small"
                        color={color}
                        sx={{ fontWeight: 'bold', minWidth: 56 }}
                      />
                      <Box sx={{ flex: 1, maxWidth: 120 }}>
                        <LinearProgress
                          variant="determinate"
                          value={country.coverageRate}
                          color={color}
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                      </Box>
                      <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
                        <Typography variant="caption" color="text.secondary">
                          {pluralize(country.attractionCount, 'attraction', 'attractions')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {country.coveredCount} covered
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {country.totalOffers.toLocaleString()} offers
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pluralize(country.cityCount, 'city', 'cities')}
                        </Typography>
                      </Stack>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {[...(country.cities ?? [])]
                      .sort((a, b) => b.attractionCount - a.attractionCount)
                      .map((city, idx) => {
                        const cityColor = coverageColor(city.coverageRate);
                        const cityPalette = theme.palette[cityColor].main;
                        const isEven = idx % 2 === 0;

                        return (
                          <Stack
                            key={`${country.countryCode}-${city.city}`}
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{
                              px: 3,
                              py: 1.25,
                              bgcolor: isEven ? 'transparent' : alpha(themeColor, 0.015),
                              borderBottom: '1px solid',
                              borderColor: alpha(themeColor, 0.06),
                              cursor: 'pointer',
                              transition: 'all 0.1s ease',
                              '&:hover': {
                                bgcolor: alpha(themeColor, 0.06),
                              },
                              '&:last-child': { borderBottom: 'none' },
                            }}
                            onClick={() => {
                              setCountryInput(country.country);
                              setCityInput(city.city);
                              setFilters((prev) => ({
                                ...prev,
                                country: country.country,
                                city: city.city,
                                offset: 0,
                              }));
                              setPaginationModel((prev) => ({ ...prev, page: 0 }));
                              setViewMode('flat');
                            }}
                          >
                            <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 200 }}>
                              {city.city}
                            </Typography>
                            <Box sx={{ flex: 1, maxWidth: 180 }}>
                              <LinearProgress
                                variant="determinate"
                                value={city.coverageRate}
                                color={cityColor}
                                sx={{ height: 8, borderRadius: 4, bgcolor: alpha(cityPalette, 0.1) }}
                              />
                            </Box>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={`${cityColor}.main`}
                              sx={{ minWidth: 50, textAlign: 'right' }}
                            >
                              {city.coverageRate}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                              {city.attractionCount} attr.
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                              {city.coveredCount} covered
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                              {city.totalOffers} offers
                            </Typography>
                          </Stack>
                        );
                      })}
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </Box>
      )}
    </Box>
  );
}
